import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/authContext";
import { useSubscription } from "@/lib/subscriptionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Eye, EyeOff, Check } from "lucide-react";
import { lovable } from "@/integrations/lovable/index";
import { useI18n } from "@/lib/i18nContext";

export default function Auth() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { subscription, loading: subLoading } = useSubscription();
  const { t } = useI18n();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const passwordRules = [
    { key: "length", label: t("auth.pwRuleLength"), test: (p: string) => p.length >= 8 },
    { key: "uppercase", label: t("auth.pwRuleUppercase"), test: (p: string) => /[A-Z]/.test(p) },
    { key: "number", label: t("auth.pwRuleNumber"), test: (p: string) => /[0-9]/.test(p) },
    { key: "special", label: t("auth.pwRuleSpecial"), test: (p: string) => /[^A-Za-z0-9]/.test(p) },
  ];
  const ruleResults = passwordRules.map(r => ({ ...r, met: r.test(password) }));
  const allRulesMet = ruleResults.every(r => r.met);

  useEffect(() => {
    if (authLoading || !user) return;
    // Wait for subscription to load so we can route paid users straight to
    // their dashboard instead of bouncing them through onboarding/plan
    // selection again.
    if (subLoading) return;
    const plan = subscription?.plan ?? "free";
    const destination = plan === "free" ? "/onboarding" : "/dashboard";
    navigate(destination, { replace: true });
  }, [user, authLoading, subscription, subLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        if (!allRulesMet) {
          throw new Error(t("auth.pwRulesNotMet"));
        }
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) throw error;
        // Admin notification is sent by AuthProvider when SIGNED_IN fires for first-ever sign-in.
        toast.success(t("auth.accountCreated"));
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="container flex items-center justify-between py-5">
        <span
          className="font-heading text-xl font-medium tracking-tight text-foreground cursor-pointer"
          onClick={() => navigate("/")}
        >
          oplif<span className="text-primary">.</span>
        </span>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-medium text-foreground">
              {isLogin ? t("auth.welcomeBack") : t("auth.createAccount")}
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {isLogin ? t("auth.signInDesc") : t("auth.signUpDesc")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm text-foreground">{t("auth.email")}</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm text-foreground">{t("auth.password")}</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-11"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {!isLogin && (
                <ul className="mt-2 space-y-1">
                  {ruleResults.map(rule => (
                    <li
                      key={rule.key}
                      className={`flex items-center gap-2 text-xs transition-colors ${
                        rule.met ? "text-green-600 dark:text-green-500" : "text-muted-foreground"
                      }`}
                    >
                      <Check className={`w-3.5 h-3.5 ${rule.met ? "opacity-100" : "opacity-40"}`} />
                      <span>{rule.label}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-11 gap-2"
              disabled={loading || (!isLogin && !allRulesMet)}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {isLogin ? t("auth.signIn") : t("auth.signUp")}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">{t("auth.or")}</span></div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-11 gap-2"
            onClick={async () => {
              const result = await lovable.auth.signInWithOAuth("google", {
                redirect_uri: window.location.origin,
              });
              if (result.error) {
                toast.error(t("auth.googleFailed"));
              }
            }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            {t("auth.continueGoogle")}
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {isLogin ? t("auth.noAccount") : t("auth.hasAccount")}{" "}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-primary font-medium hover:underline"
            >
              {isLogin ? t("auth.signUpLink") : t("auth.signInLink")}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
